import type * as React from "react";
import { cn } from "../lib/utils";

/** Label + control + hint/error, the form row of the design system (label → control 6px, fields 16px apart). */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium leading-ui">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-xs leading-ui text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs leading-ui text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/** Surface card (page level, 20px radius, hairline border). */
export function Card({ className, ...props }: React.ComponentProps<"section">) {
  return <section className={cn("rounded-2xl border border-border bg-card p-5 text-card-foreground", className)} {...props} />;
}

/** Inline status message (error / success / info) with the right ARIA role. */
export function Notice({ tone = "info", className, ...props }: React.ComponentProps<"div"> & { tone?: "info" | "error" | "success" | "warning" }) {
  const tones = {
    info: "border-border bg-muted/40",
    error: "border-destructive/40 bg-destructive/10 text-destructive",
    success: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    warning: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  };
  return <div role={tone === "error" ? "alert" : "status"} className={cn("rounded-xl border p-3 text-sm leading-ui", tones[tone], className)} {...props} />;
}

/** DEMO / SANDBOX / REPLAY label (MP §15: visible wherever an adapter is not live). */
export function EnvBadge({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300"
    >
      {children}
    </span>
  );
}
